import * as React from 'react'
import * as Redux from 'react-redux'
import { css, styled } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'
import { BREAKPOINTS, COLORS, Link, glyphs, styles as S } from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'

import { useDisplayMode } from '../../../../hooks/useDisplayMode'
import { useNavigation } from '../../../../hooks/useNavigation'
import { Project, ExtraFields } from '../../../../models/project'
import { PATHS } from '../../../../paths'
import { RootState } from '../../../../../reducers'
import { MIDDLE_TABLET_LAPTOP_BREAKPOINT } from '../../constants'

type OwnerProps = {
  className?: string
}

function OwnerComponent({ className }: OwnerProps): React.ReactElement<OwnerProps> {
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)
  const mainTag = model.project.mainTag<ExtraFields>(project)
  const navigation = useNavigation()

  const avatar = model.user.avatar(project.owner, '55x55')

  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'

  function handleClick(): void {
    const path = document.location.pathname
    if (path !== project.absolute_url) {
      navigation.to(PATHS.PROJECT, {
        scrollTarget: 'about-owner',
      })
    } else {
      document.location.hash = 'about-owner'
    }
  }

  return (
    <InfoCard className={className}>
      <InfoCardItem>
        {project.owner.is_public ? (
          <plume.Link href={project.owner.absolute_url}>
            <plume.styles.image.Avatar onClick={handleClick} src={avatar} type="medium" />
          </plume.Link>
        ) : (
          <plume.styles.image.Avatar src={avatar} type="medium" />
        )}
        <InfoCardContent>
          <S.heading.XXXS onClick={handleClick}>{model.user.username(project.owner)}</S.heading.XXXS>
          <InfoCardTags $isEditMode={isEditMode}>
            {(project.city || project.owner.location) && (
              <InfoCardTag>
                <glyphs.stroke.MapCursor size={13} />
                {project.city || project.owner.location}
              </InfoCardTag>
            )}
            {mainTag && (
              <Link kind="secondary" withIcon href={project.main_tag?.absolute_url}>
                <InfoCardTag>
                  <glyphs.stroke.Tag size={13} />
                  {mainTag}
                </InfoCardTag>
              </Link>
            )}
          </InfoCardTags>
        </InfoCardContent>
      </InfoCardItem>
    </InfoCard>
  )
}

export const Owner = styled(OwnerComponent)``

const InfoCard = styled.div<{ className?: string }>`
  ${S.heading.XXXXXS} {
    margin-bottom: 10px;
  }
`

const InfoCardTag = styled(S.heading.XXXXXS)`
  display: flex;
  align-items: center;

  ${glyphs.SizedSvg} {
    color: ${COLORS.PRIMARY_SAND_500};
  }
`

const InfoCardTags = styled.div<{ $isEditMode: boolean }>`
  display: flex;
  margin-top: 5px;

  ${Link} {
    cursor: pointer;
  }

  div + ${Link} {
    margin-left: 14px;

    ${({ $isEditMode }) => {
      if ($isEditMode) {
        return css`
          @media screen and ${BREAKPOINTS.LAPTOP} {
            margin-left: 20px;
          }
        `
      } else {
        return css`
          @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
            margin-left: 20px;
          }
        `
      }
    }}
  }
`

const InfoCardContent = styled.div`
  display: flex;
  flex-direction: column;

  ${S.heading.XXXS} {
    cursor: pointer;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    right: 0;
    top: 20px;
  }
`

const InfoCardItem = styled.div`
  display: flex;
  align-items: center;

  ${plume.styles.image.Avatar} {
    cursor: pointer;
    background-color: ${plume.COLORS.PRIMARY_GREY_000};
    margin-right: 20px;
  }
`
